<template>
  <div class="floating-item">
    <div
      v-for="item in list"
      :key="item.id"
      class="item"
      @click="handleItemClick(item)"
      :class="{ 'item-hidden': !isShowItems && item.id !== 5 }"
    >
        <span v-if="item.id === 1 && unreadCount > 0" class="unread-badge">
          {{ unreadCount }}
        </span>
      <img class="icon" :src="item.icon" alt="" />
      <div class="name" :class="{ 'has-badge': item.id === 1 && unreadCount > 0 }">
        {{ item.name }}
      </div>
    </div>
    <img class="icon"  @click="handleItemClick({id: 5, name: '',icon:''})" :src="`${isShowItems? require('@/assets/images/icons/close-circle.png') : require('@/assets/images/icons/expand-circle.png')}`" alt="" />

  </div>
</template>

<script lang='ts' setup>
import { ref, computed, getCurrentInstance, onMounted } from 'vue'
import YSF from "@neysf/qiyu-web-sdk";
import userService from '@/services/user'
import { useI18n } from 'vue-i18n';
import { ElMessage } from 'element-plus'
import useCommon from '@/composables/useCommon'
const { getUserInfo } = userService
import { useStore } from 'vuex'
const store = useStore()
import { getToken } from '@/utils/auth'
const {
  appContext: {
    config: { globalProperties }
  }
} = getCurrentInstance()
const lang = computed(() => store.state.user.language)
const { t } = useI18n();
const { JumpToHref } = useCommon()
const unreadCount = ref(0);

// 控制除关闭外的项是否显示（默认显示）
const isShowItems = ref(true);

// 悬浮按钮列表数据
const list = ref([
  {
    id: 1,
    name: `${t('msg.calculate.xskf')}`,
    icon: require('@/assets/images/icons/right_kf.png'),
  },
  {
    id: 2,
    name: `${t('msg.other.yf_ss')}`,
    icon: require('@/assets/images/icons/right_jisq.png'),
  },
  {
    id: 3,
    name: `${t('msg.calculate.scwz')}`,
    icon: require('@/assets/images/icons/right_sc.png'),
  },
  {
    id: 4,
    name: `${t('msg.calculate.top_back')}`,
    icon: require('@/assets/images/icons/right_top.png'),
  },
])

// 客服 SDK 实例（全局保存，避免重复初始化）
let customerInstance = null

/**
 * 初始化客服 SDK
 */
const initCustomerService = async () => {
  if (customerInstance) return customerInstance // 已初始化则直接返回

  try {
    // 初始化 SDK
    const ysf = await YSF.init('45656fef3487f900cbe100fb04c8057c', {
      sdkTemplateId: 8128650,
      templateId: 8128650,
      sessionInvite: false,
      hidden: 1
    })

    // 保存实例到全局
    globalProperties.$customer = ysf
    customerInstance = ysf
    ysf('onunread', (res) => {
      unreadCount.value = res.total
    })
    // 配置用户信息（如果已登入）
    const token = store.state.user.token || getToken()
    if (token) {
      try {
        const res = await getUserInfo()
        const userInfo = res.data.result
        store.dispatch('user/setUserInfo', userInfo)

        // 设置用户信息
        ysf('config', {
          uid: userInfo.user_id,
          nickname: `${userInfo.user_id}-买到手`,
          email: userInfo.email,
          mobile_phone: userInfo.mobile,
          data: JSON.stringify([
            { "key": "uid", "value": userInfo.user_id, "label": "UserId" },
            { "key": "nickname", "value": `${userInfo.user_id}-买到手`, "label": "昵称" },
            { "key": "mobile_phone", "value": userInfo.mobile, "label": "手机号码" },
            { "key": "email", "value": userInfo.email, "label": "邮箱" },
          ]),
          language: lang.value === 'zh-CN' ? 'zh-cn' : 'zh-tw',
        })
      } catch (err) {
        console.error('获取用户信息失败', err)
      }
    } else {
      // 未登入状态下仅配置语言
      ysf('config', {
        language: lang.value === 'zh-CN' ? 'zh-cn' : 'zh-tw'
      })
    }

    return ysf
  } catch (error) {
    console.error('客服 SDK 初始化失败', error)
    throw error // 抛出错误，方便调用处处理
  }
}

/**
 * 处理按钮点击事件
 * @param item 当前点击的按钮数据
 */
async function handleItemClick(item: { id: number; name: string; icon: any }) {
  switch (item.id) {
    case 1:
      // 线上客服：初始化并打开对话窗口
      try {
        const ysf = await initCustomerService()
        ysf('open') // 直接打开客服窗口
        unreadCount.value = 0
      } catch (error) {
        console.error('打开客服失败', error)
      }
      break;
    case 2:
      // 试算器逻辑
      JumpToHref("Calculate")
      break;
    case 3:
      // 显示提示，引导用户使用对应快捷键
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') !== -1;
      const shortcut = isMac ? 'Command + D' : 'Ctrl + D';
      ElMessage({
        message: `请使用快捷键 ${shortcut} 将本页添加到书签`,
        type: 'info',
        duration: 3000,
        showClose: true
      });
      break;
    case 4:
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      break;
    case 5:
      isShowItems.value = !isShowItems.value;
      item.icon = isShowItems.value
        ? require('@/assets/images/icons/close-circle.png')
        : require('@/assets/images/icons/expand-circle.png'); // 替换为你的展开图标
      break;
    default:
      break;
  }
}

// 页面加载时提前初始化客服 SDK（可选，优化点击响应速度）
onMounted(() => {
  initCustomerService().catch(() => {
    // 初始化失败不影响页面，点击时会再次尝试
  })
})
</script>

<style lang='scss' scoped>
.floating-item {
  position: fixed;
  right: 20px;
  bottom: 10%;
  transform: translateY(-50%);
  z-index: 1000;
  cursor: pointer;
   align-items: center;
    display: flex;
    flex-direction: column;

  .item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    margin-bottom: 15px;
    text-align: center;
    padding: 6px 15px;
    background: #FDFAF1;
    position: relative;
    // 核心：缓慢过渡（透明度+高度+margin），时长1秒，缓动更柔和
    transition: all 1s cubic-bezier(0.25, 0.1, 0.25, 1);
    // 默认状态：不透明、正常高度
    opacity: 1;
    height: auto;

    // hover 仅保留轻微背景色变化，无缩放/阴影
    &:hover {
      background: #f2f2f2;
    }

    .name {
      font-size: 12px;
      color: #333333;
      text-align: center;
      position: relative;
    }

    .unread-badge {
      position: absolute;
      top: -3px;
      right: -5px;
      width: 20px;
      height: 20px;
      line-height: 20px;
      border-radius: 50%;
      background: #ff4d4f;
      color: white;
      font-size: 12px;
      text-align: center;
      // 未读角标仅保留基础样式，去掉呼吸动画
    }

    .icon {
      width: 30px;
      height: 30px;
      margin-bottom: 5px;
      cursor: pointer;
    }

    // 隐藏状态：透明、高度收缩、margin消失
    &.item-hidden {
      opacity: 0;
      height: 0;
      margin-bottom: 0;
      padding: 0 15px; // 仅保留左右padding，避免宽度突变
      pointer-events: none; // 隐藏后无法点击
    }
  }
}
</style>